import { ArrowRight, ExternalLink, Lock, Sparkles } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

const AnimatedCounter = ({ target, duration = 2000 }: { target: number; duration?: number }) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(0);
  const observerRef = useRef<HTMLSpanElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    let startTime: number | null = null;

    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);

      const easedProgress = 1 - (1 - progress) * (1 - progress);
      const nextCount = Math.floor(easedProgress * target);

      if (nextCount !== countRef.current) {
        countRef.current = nextCount;
        setCount(nextCount);
      }

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [isVisible, target, duration]);

  return <span ref={observerRef}>{count}</span>;
};

// Operating footprint — factual setup values (not outcome metrics).
// Real performance metrics (GMV, repeat rate) are shared on request.
const PLACEHOLDER_METRICS = [
  { value: 2, label: 'Brands in the portfolio', suffix: '' },
  { value: 3, label: 'Marketplaces covered', suffix: '' },
  { value: 3, label: 'Fulfillment partners', suffix: '' },
  { value: 60, label: 'CX first-response target', suffix: 's' },
];

const OPS_STACK_NODES = [
  'In-house Storefront',
  'AI Design Pipeline',
  'SEO Listing Engine',
  'Marketplace Sync',
  'Fulfillment Router',
  'CX Agent (LLM)',
  'Reviews & Reputation',
  'Brand Analytics',
];

const UseCases = () => {
  return (
    <section className="py-20 px-6 bg-gray-950" id="use-cases">
      <div className="max-w-7xl mx-auto">
        {/* By the Numbers */}
        <div className="mb-24">
          <div className="bg-gradient-to-r from-purple-500/20 via-pink-500/20 to-blue-500/20 rounded-3xl p-12 md:p-16 border border-purple-500/30 relative overflow-hidden">
            <div className="absolute inset-0 bg-gray-950/50" />
            <div className="relative text-center mb-12">
              <h2 className="text-purple-400 text-xl mb-4 tracking-widest">OPERATING FOOTPRINT</h2>
              <p className="text-gray-400 text-sm">
                AITOS holds 2 brands under operations today (Elzary and Avyra Atlas) with the
                infrastructure to take on more. Detailed performance numbers (GMV, repeat rate)
                shared on request.
              </p>
            </div>
            <div className="relative grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
              {PLACEHOLDER_METRICS.map((stat, index) => (
                <div key={`metric-${index + 1}`} className="text-center">
                  <div className="text-5xl md:text-6xl lg:text-7xl font-bold bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent mb-2">
                    <AnimatedCounter target={stat.value} />
                    {stat.suffix}
                  </div>
                  <p className="text-gray-300 font-medium text-sm md:text-base">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Brand Portfolio Header */}
        <div className="text-center mb-16">
          <h2 className="text-purple-400 text-xl mb-4 tracking-widest">BRAND PORTFOLIO</h2>
          <h3 className="text-5xl font-bold mb-3">Brands we operate end-to-end.</h3>
          <p className="text-gray-300 max-w-3xl mx-auto">
            Each brand is operated by AITOS Labs LLC. We own the operations; brand IP belongs to the
            brand entity.
          </p>
        </div>

        {/* Brand portfolio: 2 active brands + 1 reserved slot */}
        <div className="grid md:grid-cols-3 gap-8 mb-24">
          {/* Elzary */}
          <a
            href="/brands/elzary"
            className="group bg-gradient-to-br from-purple-900/30 to-pink-900/20 rounded-2xl overflow-hidden border border-purple-500/30 hover:border-purple-500/60 transition-all duration-300 hover:transform hover:-translate-y-1 hover:shadow-2xl"
          >
            <div className="p-8 flex flex-col h-full">
              <div className="flex items-center gap-2 mb-4">
                <span className="px-3 py-1 rounded-full bg-green-500/20 text-green-400 text-xs font-medium">
                  Active
                </span>
              </div>
              <h4 className="text-3xl font-bold mb-2">Elzary</h4>
              <p className="text-purple-300 mb-4">POD apparel & lifestyle</p>
              <p className="text-gray-300 mb-6 flex-grow text-sm">
                Design, listing, fulfillment, CX, and growth — handled end-to-end on the AITOS
                in-house storefront stack.
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <span className="inline-flex items-center gap-2 text-white font-medium text-sm group-hover:gap-3 transition-all">
                  Read case study
                  <ArrowRight size={16} />
                </span>
                <a
                  href="https://elzary.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs text-gray-300 hover:bg-white/10 transition-colors"
                >
                  elzary.com
                  <ExternalLink size={10} />
                </a>
              </div>
            </div>
          </a>

          {/* Avyra Atlas */}
          <a
            href="/brands/avyraatlas"
            className="group bg-gradient-to-br from-pink-900/30 to-amber-900/20 rounded-2xl overflow-hidden border border-pink-500/30 hover:border-pink-500/60 transition-all duration-300 hover:transform hover:-translate-y-1 hover:shadow-2xl"
          >
            <div className="p-8 flex flex-col h-full">
              <div className="flex items-center gap-2 mb-4">
                <span className="px-3 py-1 rounded-full bg-green-500/20 text-green-400 text-xs font-medium">
                  Active
                </span>
              </div>
              <h4 className="text-3xl font-bold mb-2">Avyra Atlas</h4>
              <p className="text-pink-300 mb-4">Print-on-Demand lifestyle</p>
              <p className="text-gray-300 mb-6 flex-grow text-sm">
                Operated on the same AITOS in-house storefront stack, with multi-marketplace
                distribution and AI-assisted ops.
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <span className="inline-flex items-center gap-2 text-white font-medium text-sm group-hover:gap-3 transition-all">
                  Read case study
                  <ArrowRight size={16} />
                </span>
                <a
                  href="https://avyraatlas.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs text-gray-300 hover:bg-white/10 transition-colors"
                >
                  avyraatlas.com
                  <ExternalLink size={10} />
                </a>
              </div>
            </div>
          </a>

          {/* Reserved slot for the next brand */}
          <div className="bg-gray-900/50 rounded-2xl overflow-hidden border border-dashed border-gray-700 flex flex-col items-center justify-center text-center p-8">
            <div className="w-14 h-14 rounded-full bg-gray-800 flex items-center justify-center mb-4">
              <Lock size={24} className="text-gray-500" />
            </div>
            <h4 className="text-xl font-bold mb-2 text-gray-400">Next brand</h4>
            <p className="text-sm text-gray-500">Launching soon.</p>
          </div>
        </div>

        {/* Ops Stack */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <h2 className="text-purple-400 text-xl mb-4 tracking-widest">THE AITOS OPS STACK</h2>
            <h3 className="text-4xl md:text-5xl font-bold mb-3">
              One stack. Every brand we operate.
            </h3>
            <p className="text-gray-300 max-w-3xl mx-auto">
              Proprietary tooling, tuned by humans who run the brands themselves.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {OPS_STACK_NODES.map((node, idx) => (
              <div
                key={node}
                className="bg-gray-900 rounded-xl p-5 border border-gray-800 hover:border-purple-500/30 transition-all duration-300 flex items-center gap-3"
              >
                <div className="w-9 h-9 rounded-lg bg-purple-500/10 flex items-center justify-center flex-shrink-0">
                  <Sparkles size={16} className="text-purple-400" />
                </div>
                <span className="text-sm font-medium text-gray-200">{node}</span>
                <span className="ml-auto text-xs text-gray-600">
                  {String(idx + 1).padStart(2, '0')}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* For sellers split section */}
        <div className="bg-gray-900 rounded-3xl p-10 md:p-14 border border-gray-800 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <h3 className="text-3xl md:text-4xl font-bold mb-3 leading-tight">
              Already have a POD brand?
              <br />
              <span className="bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">
                We can operate it for you.
              </span>
            </h3>
          </div>
          <div className="space-y-4">
            <p className="text-gray-300 text-lg">
              AITOS can take over operations end-to-end — design pipeline, listings, fulfillment,
              CX, growth — while you keep ownership of the brand.
            </p>
            <a
              href="/services"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium hover:opacity-90 transition-opacity"
            >
              See the managed-service offering
              <ArrowRight size={18} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default UseCases;
